/**
 * useClientDocumentRows — the Client → Dokumente tab (dms/spec.md §4.2, §M12).
 *
 * "Lists that client's documents, latest version only" — `GET /documents`
 * defaults to `state='active'` server-side (§M6), so this simply scopes by
 * `client_id` and never opts into `include_inactive`; the Ablage grid stays
 * the ONLY surface that also shows historized/inactive documents.
 *
 * live mode → GET /documents?client_id=&state=active via apiClient.documents.list.
 * mock mode → the existing per-client entity-detail fixture (`dokumenteForKunde`,
 *             already keyed by kundeId), mapped to the same row shape so the
 *             tab renders one grid for both modes (usePoliceDetail precedent).
 *
 * [Source: apps/web/src/features/documents/hooks/useDocumentRows.ts] (live/mock hook precedent)
 * [Source: apps/web/src/features/entity-detail/mock.ts#dokumenteForKunde]
 * [Source: projects/broker-ag/dms/spec.md §4.2, §M12]
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { dokumenteForKunde } from '../../entity-detail/mock';
import { dateShortDe } from '../../../lib/format';
import { sizeDisplay } from '../api/documents-api';

/** One row of the Client → Dokumente tab (a display-shaped subset of DocumentListItemDto). */
export interface ClientDocumentRow {
  id: string;
  title: string;
  category: string | null;
  quelle: string;
  dateDisplay: string;
  sizeDisplay: string;
  /** When the document reached Brokly (ISO instant, live only): the P6 dialog prefills its Eingangsdatum. */
  receivedAt?: string;
}

export function useClientDocumentRows(clientId: string) {
  const live = isLiveData();

  const query = useQuery<ClientDocumentRow[]>({
    queryKey: qk.clientDocuments(clientId),
    queryFn: async () => {
      if (!live) {
        return dokumenteForKunde(clientId).map((d) => ({
          id: d.id,
          title: d.name,
          category: d.typ,
          quelle: '-',
          dateDisplay: d.datum,
          sizeDisplay: '-',
        }));
      }
      const res = await apiClient.documents.list({ client_id: clientId, state: 'active' });
      return res.items.map((it) => ({
        id: it.id,
        title: it.title,
        category: it.category,
        quelle: it.quelle,
        dateDisplay: dateShortDe(it.doc_date),
        sizeDisplay: sizeDisplay(it.size_bytes),
        receivedAt: it.doc_date,
      }));
    },
    enabled: clientId.length > 0,
    ...(live ? {} : { staleTime: Infinity }),
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
